import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminGetContactSubmissions, adminUpdateContactSubmissionStatus, adminDeleteContactSubmission, adminDownloadSubmissionFile } from '../../api/adminApi';
import { ContactSubmission } from '../../api/contactApi';
import { useAdminContext } from '../../contexts/AdminContext';

const STATUS_OPTIONS = [
  { value: 'new',         label: 'Neu' },
  { value: 'updated',     label: 'Aktualisiert' },
  { value: 'in-progress', label: 'In Bearbeitung' },
  { value: 'done',        label: 'Erledigt' },
  { value: 'archived',    label: 'Archiviert' },
];

const STATUS_BADGE: Record<string, string> = {
  new:         'bg-red-100 text-red-700',
  updated:     'bg-blue-100 text-blue-700',
  'in-progress': 'bg-yellow-100 text-yellow-700',
  done:        'bg-green-100 text-green-700',
  archived:    'bg-gray-100 text-gray-500',
};

const AdminInquiries: React.FC = () => {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const { refreshInquiriesCount } = useAdminContext();

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminGetContactSubmissions();
      setSubmissions(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  const handleStatusChange = async (id: string, newStatus: string) => {
    setUpdating(id);
    try {
      const updated = await adminUpdateContactSubmissionStatus(id, newStatus);
      setSubmissions((prev) => prev.map((s) => (s.id === id ? updated : s)));
      refreshInquiriesCount();
    } catch (err: any) {
      setError(`Fehler beim Aktualisieren: ${err.message}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich löschen?')) return;
    try {
      await adminDeleteContactSubmission(id);
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
      if (selectedId === id) setSelectedId(null);
      refreshInquiriesCount();
    } catch (err: any) {
      setError(`Fehler beim Löschen: ${err.message}`);
    }
  };

  const handleDownload = async (submissionId: string, blobPath: string) => {
    setDownloading(blobPath);
    try {
      await adminDownloadSubmissionFile(submissionId, blobPath);
    } catch (err: any) {
      setError(`Fehler beim Herunterladen: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const selected = submissions.find((s) => s.id === selectedId) ?? null;

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('de-CH', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return <p className="text-gray-500">Laden…</p>;
  }

  if (error) {
    return <div className="text-red-600 bg-red-50 border border-red-200 rounded p-4">{error}</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Kontaktanfragen</h1>

      {submissions.length === 0 ? (
        <p className="text-gray-500">Keine Einträge vorhanden.</p>
      ) : (
        <div className="flex gap-6">
          {/* List */}
          <div className="flex-1 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600 border-b border-gray-200">
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">E-Mail</th>
                  <th className="px-3 py-2 font-medium">Rechtsgebiet</th>
                  <th className="px-3 py-2 font-medium">Betreff</th>
                  <th className="px-3 py-2 font-medium">Datum</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelectedId(s.id === selectedId ? null : s.id)}
                    className={`border-b border-gray-100 cursor-pointer hover:bg-primary-50 transition ${
                      s.id === selectedId ? 'bg-primary-50' : ''
                    }`}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={s.status}
                        onChange={(e) => handleStatusChange(s.id, e.target.value)}
                        disabled={updating === s.id}
                        className={`text-xs border border-gray-300 rounded px-2 py-1 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none font-medium ${STATUS_BADGE[s.status] ?? ''}`}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">{s.name}</td>
                    <td className="px-3 py-2 text-gray-600">{s.email}</td>
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {s.areaOfLaw ?? '—'}
                      {s.source === 'booking' && (
                        <span className="ml-1 inline-block bg-primary-50 text-primary-700 rounded-full px-1.5 py-0.5 text-[10px] align-middle">Termin</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-xs truncate">{s.subject}</td>
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{formatDate(s.submittedAt)}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                        title="Löschen"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Detail panel */}
          {selected && (
            <div className="w-96 bg-white border border-gray-200 rounded-lg shadow-sm p-6 shrink-0">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Detail</h2>
                <button
                  onClick={() => setSelectedId(null)}
                  className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                  aria-label="Schliessen"
                >
                  &times;
                </button>
              </div>

              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500 font-medium">Name</dt>
                  <dd className="text-gray-800">{selected.name}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">E-Mail</dt>
                  <dd>
                    <a href={`mailto:${selected.email}`} className="text-primary-600 hover:underline">
                      {selected.email}
                    </a>
                  </dd>
                </div>
                {selected.phone && (
                  <div>
                    <dt className="text-gray-500 font-medium">Telefon</dt>
                    <dd className="text-gray-800">{selected.phone}</dd>
                  </div>
                )}
                {selected.areaOfLaw && (
                  <div>
                    <dt className="text-gray-500 font-medium">Rechtsgebiet</dt>
                    <dd className="text-gray-800">{selected.areaOfLaw}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500 font-medium">Betreff</dt>
                  <dd className="text-gray-800">{selected.subject}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Nachricht</dt>
                  <dd className="text-gray-800 whitespace-pre-wrap">{selected.message}</dd>
                </div>

                {/* Bookings linked via the CRM link (see BookingCrmService) */}
                {selected.bookingIds != null && selected.bookingIds.length > 0 && (
                  <div>
                    <dt className="text-gray-500 font-medium mb-1">
                      Verknüpfte Termine ({selected.bookingIds.length})
                    </dt>
                    <dd className="flex flex-wrap gap-1">
                      {selected.bookingIds.map((bookingId) => (
                        <Link
                          key={bookingId}
                          to="/admin/bookings"
                          title={bookingId}
                          className="inline-block bg-primary-50 text-primary-700 border border-primary-200 rounded-full px-2 py-0.5 text-xs hover:bg-primary-100 transition"
                        >
                          Termin ansehen
                        </Link>
                      ))}
                    </dd>
                  </div>
                )}

                {/* Initial attachments */}
                {selected.attachments?.length > 0 && (
                  <div>
                    <dt className="text-gray-500 font-medium mb-1">Anhänge (initial)</dt>
                    <dd>
                      <ul className="space-y-1">
                        {selected.attachments.map((path) => {
                          const displayName = path.split('/').pop()?.replace(/^[a-f0-9]{32}-/, '') ?? path;
                          return (
                            <li key={path}>
                              <button
                                onClick={() => handleDownload(selected.id, path)}
                                disabled={downloading === path}
                                className="text-primary-600 hover:underline text-xs disabled:opacity-50"
                              >
                                {downloading === path ? '⏳ ' : '⬇ '}{displayName}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </dd>
                  </div>
                )}

                {/* Follow-up updates */}
                {selected.updates?.length > 0 && (
                  <div>
                    <dt className="text-gray-500 font-medium mb-1">
                      Kundennachrichten ({selected.updates.length})
                    </dt>
                    <dd>
                      <ul className="space-y-3">
                        {selected.updates.map((u, i) => (
                          <li key={i} className="bg-blue-50 border border-blue-100 rounded p-3 text-xs">
                            <p className="text-gray-400 mb-1">{formatDate(u.timestamp)}</p>
                            {u.message && (
                              <p className="text-gray-800 whitespace-pre-wrap">{u.message}</p>
                            )}
                            {u.attachments?.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {u.attachments.map((path) => {
                                  const displayName = path.split('/').pop()?.replace(/^[a-f0-9]{32}-/, '') ?? path;
                                  return (
                                    <li key={path}>
                                      <button
                                        onClick={() => handleDownload(selected.id, path)}
                                        disabled={downloading === path}
                                        className="text-primary-600 hover:underline text-xs disabled:opacity-50"
                                      >
                                        {downloading === path ? '⏳ ' : '⬇ '}{displayName}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}

                <div>
                  <dt className="text-gray-500 font-medium">Sprache</dt>
                  <dd className="text-gray-800">{selected.preferredLanguage}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Einwilligung</dt>
                  <dd className={selected.consentGiven ? 'text-green-600' : 'text-red-600'}>
                    {selected.consentGiven ? 'Ja' : 'Nein'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500 font-medium">Datum</dt>
                  <dd className="text-gray-800">{formatDate(selected.submittedAt)}</dd>
                </div>
                {selected.ipAddress && (
                  <div>
                    <dt className="text-gray-500 font-medium">IP-Adresse</dt>
                    <dd className="text-gray-400 font-mono text-xs">{selected.ipAddress}</dd>
                  </div>
                )}
              </dl>

              <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
                <select
                  value={selected.status}
                  onChange={(e) => handleStatusChange(selected.id, e.target.value)}
                  disabled={updating === selected.id}
                  className="text-sm border border-gray-300 rounded px-3 py-2 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => handleDelete(selected.id)}
                  className="text-sm bg-red-50 text-red-600 hover:bg-red-100 px-3 py-2 rounded transition"
                >
                  Löschen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;
